/**
 * Nexus Relationship Store
 *
 * Zustand, DB-first. No localStorage.
 * Keyed by workspaceId — loads active proposals and reviewed history.
 *
 * Actions:
 *   fetchProposals(workspaceId)            — load active proposals
 *   fetchHistory(workspaceId, limit?)      — load accepted/dismissed history
 *   accept(proposalId, userId, workspaceId) — mark as accepted (RPC)
 *   dismiss(proposalId, userId, workspaceId) — mark as dismissed (RPC)
 */

import { create } from 'zustand';
import {
  getNexusProposals,
  getNexusProposalHistory,
  acceptNexusProposal,
  dismissNexusProposal,
  type NexusRelationshipProposal,
} from '../../lib/nexusAgentService';

// ── Per-workspace state ───────────────────────────────────────────────────────

interface NexusWorkspaceState {
  proposals: NexusRelationshipProposal[];
  history: NexusRelationshipProposal[];
  loading: boolean;
  historyLoading: boolean;
  error: string | null;
  /** Whether an accept/dismiss mutation is in progress */
  mutating: boolean;
}

function emptyWorkspaceState(): NexusWorkspaceState {
  return {
    proposals:      [],
    history:        [],
    loading:        false,
    historyLoading: false,
    error:          null,
    mutating:       false,
  };
}

// ── Store state + actions ─────────────────────────────────────────────────────

interface NexusRelationshipStoreState {
  /** Active proposals + history keyed by workspaceId */
  byWorkspace: Record<string, NexusWorkspaceState>;

  /** Load active proposals for a workspace from the DB. */
  fetchProposals: (workspaceId: string) => Promise<void>;

  /** Load accepted/dismissed history for a workspace. */
  fetchHistory: (workspaceId: string, limit?: number) => Promise<void>;

  /** Mark a proposal as accepted. */
  accept: (params: {
    proposalId: string;
    userId: string;
    workspaceId: string;
  }) => Promise<{ success: boolean; error?: string }>;

  /** Mark a proposal as dismissed. */
  dismiss: (params: {
    proposalId: string;
    userId: string;
    workspaceId: string;
  }) => Promise<{ success: boolean; error?: string }>;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useNexusRelationshipStore = create<NexusRelationshipStoreState>((set, _get) => ({
  byWorkspace: {},

  // ── fetchProposals ──────────────────────────────────────────────────────────

  fetchProposals: async (workspaceId) => {
    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          ...(s.byWorkspace[workspaceId] ?? emptyWorkspaceState()),
          loading: true,
          error:   null,
        },
      },
    }));

    const { data, error } = await getNexusProposals(workspaceId, 'active');

    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          ...(s.byWorkspace[workspaceId] ?? emptyWorkspaceState()),
          loading:   false,
          proposals: data,
          error:     error,
        },
      },
    }));
  },

  // ── fetchHistory ────────────────────────────────────────────────────────────

  fetchHistory: async (workspaceId, limit = 50) => {
    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          ...(s.byWorkspace[workspaceId] ?? emptyWorkspaceState()),
          historyLoading: true,
        },
      },
    }));

    const { data, error } = await getNexusProposalHistory(workspaceId, limit);

    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          ...(s.byWorkspace[workspaceId] ?? emptyWorkspaceState()),
          historyLoading: false,
          history:        error ? (s.byWorkspace[workspaceId]?.history ?? []) : data,
        },
      },
    }));
  },

  // ── accept ──────────────────────────────────────────────────────────────────

  accept: async ({ proposalId, userId, workspaceId }) => {
    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          ...(s.byWorkspace[workspaceId] ?? emptyWorkspaceState()),
          mutating: true,
          error:    null,
        },
      },
    }));

    const { error } = await acceptNexusProposal(proposalId, userId);

    if (!error) {
      set((s) => {
        const ws = s.byWorkspace[workspaceId] ?? emptyWorkspaceState();
        const accepted = ws.proposals.find((p) => p.id === proposalId);
        return {
          byWorkspace: {
            ...s.byWorkspace,
            [workspaceId]: {
              ...ws,
              mutating:  false,
              proposals: ws.proposals.filter((p) => p.id !== proposalId),
              history:   accepted
                ? [{ ...accepted, status: 'accepted' as const }, ...ws.history]
                : ws.history,
            },
          },
        };
      });
      return { success: true };
    }

    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          ...(s.byWorkspace[workspaceId] ?? emptyWorkspaceState()),
          mutating: false,
          error,
        },
      },
    }));
    return { success: false, error };
  },

  // ── dismiss ─────────────────────────────────────────────────────────────────

  dismiss: async ({ proposalId, userId, workspaceId }) => {
    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          ...(s.byWorkspace[workspaceId] ?? emptyWorkspaceState()),
          mutating: true,
          error:    null,
        },
      },
    }));

    const { error } = await dismissNexusProposal(proposalId, userId);

    if (!error) {
      set((s) => {
        const ws = s.byWorkspace[workspaceId] ?? emptyWorkspaceState();
        const dismissed = ws.proposals.find((p) => p.id === proposalId);
        return {
          byWorkspace: {
            ...s.byWorkspace,
            [workspaceId]: {
              ...ws,
              mutating:  false,
              proposals: ws.proposals.filter((p) => p.id !== proposalId),
              history:   dismissed
                ? [{ ...dismissed, status: 'dismissed' as const }, ...ws.history]
                : ws.history,
            },
          },
        };
      });
      return { success: true };
    }

    set((s) => ({
      byWorkspace: {
        ...s.byWorkspace,
        [workspaceId]: {
          ...(s.byWorkspace[workspaceId] ?? emptyWorkspaceState()),
          mutating: false,
          error,
        },
      },
    }));
    return { success: false, error };
  },
}));
